import { useEffect, useRef } from 'react';
import { api } from '../api';
import { useLang, isLang, type Lang } from '../i18n';
import { useAuth } from './AuthContext';

/**
 * Bridges the language preference between localStorage (pre-login) and the user
 * profile (post-login). Renders nothing.
 *
 * - On sign-in: adopt the language saved in the user's profile (so it follows them
 *   across devices), once per signed-in user — later manual switches are respected.
 * - While signed in: persist any language change to the profile (PATCH /me), in
 *   addition to the localStorage write the provider already does.
 *
 * Refs prevent a ping-pong between the two effects (applying the profile value
 * would otherwise re-trigger a redundant persist).
 */
export function LanguageSync() {
  const { lang, setLang } = useLang();
  const { user } = useAuth();
  const appliedForUser = useRef<string | null>(null);
  /** The language we believe is stored in the profile (avoids redundant writes). */
  const profileLang = useRef<Lang | null>(null);

  // Apply the profile language to the UI once per signed-in user.
  useEffect(() => {
    if (!user) {
      appliedForUser.current = null;
      profileLang.current = null;
      return;
    }
    if (appliedForUser.current === user.id) return;
    appliedForUser.current = user.id;
    const pref = isLang(user.language) ? user.language : null;
    profileLang.current = pref;
    if (pref && pref !== lang) setLang(pref);
  }, [user, lang, setLang]);

  // Persist UI language to the profile whenever it diverges (while signed in).
  useEffect(() => {
    if (!user) return;
    if (profileLang.current === lang) return;
    profileLang.current = lang;
    void api.me.update({ language: lang }).catch(() => {
      /* best-effort; localStorage already holds the choice */
    });
  }, [lang, user]);

  return null;
}
